/**
 * HeadlineBand — 5-stat 3-2 grid. Spec §A.
 *
 * Dark gradient section at the top of the Progress tab.
 * Top row: Issues fixed · Daily visits gained · Overall score
 * Bottom row: AI citation rate · AI mention share
 *
 * Grid layout uses inline styles for grid-template-areas (not supported
 * natively in Tailwind without arbitrary values).
 *
 * WCAG 2.2 AA: all interactive elements keyboard-reachable, tooltips use
 * role="tooltip" + aria-describedby.
 *
 * §F.3: tabular-nums applied via font-variant-numeric on number cells.
 */

import type { ReactNode } from 'react';
import { t } from '../../i18n';

// ── Info tooltip ─────────────────────────────────────────────────────────────

interface InfoButtonProps {
  id: string;
  description: string;
  glossaryAnchor?: string;
}

/**
 * CSS-driven accessible tooltip (no JS state required).
 * Show/hide via group-hover + group-focus-within on the wrapper span.
 */
function InfoButton({ id, description, glossaryAnchor }: InfoButtonProps) {
  const tooltipId = `tooltip-${id}`;
  return (
    <span className="relative inline-flex group/info">
      <button
        type="button"
        aria-describedby={tooltipId}
        className="text-white/50 hover:text-white/85 focus-visible:text-white/85 transition-colors p-0 bg-transparent border-0 cursor-help leading-none"
      >
        <svg
          className="w-3 h-3"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="8" cy="8" r="7.5" stroke="currentColor" strokeWidth="1" fill="none" />
          <text x="8" y="12" textAnchor="middle" fontSize="10" fontWeight="bold">i</text>
        </svg>
        <span className="sr-only">More information</span>
      </button>
      <div
        id={tooltipId}
        role="tooltip"
        className="hidden group-hover/info:block group-focus-within/info:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-slate-900 text-white text-xs rounded-lg p-3 shadow-xl z-50 pointer-events-auto"
      >
        <p className="leading-relaxed">{description}</p>
        {glossaryAnchor && (
          <a
            href={`/glossary#${glossaryAnchor}`}
            className="mt-2 block text-emerald-400 hover:text-emerald-300 font-medium"
          >
            {t('tooltip.learnMore', 'Learn more →')}
          </a>
        )}
        {/* tooltip caret */}
        <div
          aria-hidden="true"
          className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[5px] border-x-transparent border-t-[5px] border-t-slate-900"
        />
      </div>
    </span>
  );
}

// ── Stat cell ─────────────────────────────────────────────────────────────────

interface StatCellProps {
  gridArea: string;
  num: ReactNode;
  label: string;
  bottom?: boolean;
  infoId?: string;
  infoDescription?: string;
  glossaryAnchor?: string;
  newBadge?: boolean;
}

function StatCell({
  gridArea,
  num,
  label,
  bottom = false,
  infoId,
  infoDescription,
  glossaryAnchor,
  newBadge = false,
}: StatCellProps) {
  return (
    <div
      style={{ gridArea }}
      className={bottom ? 'pt-6 border-t border-white/[0.08]' : undefined}
    >
      {/* Number row */}
      <div
        className="text-4xl font-semibold tracking-tight text-white leading-none flex items-baseline gap-1.5"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {num}
      </div>
      {/* Label row */}
      <div className="text-xs text-white/65 mt-2 tracking-[0.02em] flex items-center gap-1.5">
        <span>{label}</span>
        {infoId && infoDescription && (
          <InfoButton
            id={infoId}
            description={infoDescription}
            {...(glossaryAnchor !== undefined ? { glossaryAnchor } : {})}
          />
        )}
        {newBadge && (
          <span
            aria-label="New feature"
            className="text-[9px] font-bold tracking-[0.08em] px-1.5 py-0.5 bg-emerald-500/[0.18] text-emerald-400 rounded-[3px] uppercase"
          >
            {t('badge.new', 'New')}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Arrow separator ───────────────────────────────────────────────────────────

function Arrow() {
  return (
    <span
      aria-label="to"
      style={{ fontSize: 14, fontWeight: 600, color: '#10B981' }}
    >
      →
    </span>
  );
}

function PctSuffix() {
  return (
    <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}>%</span>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

export interface HeadlineBandProps {
  issuesFixed: number;
  visitsGained: number;
  thenScore: number | null;
  nowScore: number | null;
  thenCitationRate: number | null;
  nowCitationRate: number | null;
  thenAiMentionShare: number | null;
  nowAiMentionShare: number | null;
  /** Server-generated narrative (≤200 chars). Falls back to static template. */
  narrative: string | null;
  /** Renders as a single column instead of 3-2 grid (mobile). */
  mobile?: boolean;
}

export function HeadlineBand({
  issuesFixed,
  visitsGained,
  thenScore,
  nowScore,
  thenCitationRate,
  nowCitationRate,
  thenAiMentionShare,
  nowAiMentionShare,
  narrative,
  mobile = false,
}: HeadlineBandProps) {
  const displayNarrative =
    narrative ??
    t(
      'headline.narrative.fallback',
      `You've fixed {issues} issues and AI assistants now cite you {rate}% of the time.`,
      {
        issues: issuesFixed,
        rate: nowCitationRate ?? 0,
      },
    );

  // ── Stat number nodes ─────────────────────────────────────────────────

  const issuesNum = <span>{issuesFixed}</span>;

  const visitsNum = (
    <span>
      {visitsGained >= 0 ? `+${visitsGained}` : `${visitsGained}`}
    </span>
  );

  const scoreNum =
    thenScore !== null && nowScore !== null ? (
      <>
        <span>{thenScore}</span>
        <Arrow />
        <span>{nowScore}</span>
      </>
    ) : (
      <span>{nowScore ?? '—'}</span>
    );

  const citationNum =
    thenCitationRate !== null || nowCitationRate !== null ? (
      <>
        <span>{thenCitationRate !== null ? thenCitationRate : '—'}</span>
        <Arrow />
        <span>{nowCitationRate !== null ? nowCitationRate : '—'}</span>
        {nowCitationRate !== null && <PctSuffix />}
      </>
    ) : (
      <span>—</span>
    );

  const mentionNum =
    nowAiMentionShare !== null ? (
      <>
        {thenAiMentionShare !== null && (
          <>
            <span>{thenAiMentionShare}</span>
            <Arrow />
          </>
        )}
        <span>{nowAiMentionShare}</span>
        <PctSuffix />
      </>
    ) : (
      <span aria-label={t('headline.mention.unavailable', 'Not yet available')}>—</span>
    );

  // ── Grid layout ───────────────────────────────────────────────────────

  const gridStyle: React.CSSProperties = mobile
    ? {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateAreas: '"issues" "visits" "score" "citation" "mention"',
        gap: 20,
      }
    : {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'auto auto',
        gridTemplateAreas: '"issues visits score" "citation mention ."',
        gap: 32,
      };

  return (
    <section
      aria-labelledby="headline-band-title"
      className="rounded-2xl relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        padding: mobile ? '24px 20px' : '32px 36px',
        marginBottom: mobile ? 16 : 28,
      }}
    >
      {/* decorative radial glow — aria-hidden */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          right: -80,
          top: -80,
          width: 280,
          height: 280,
          background:
            'radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%)',
        }}
      />

      <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-emerald-400 opacity-90 mb-2 relative">
        {t('headline.eyebrow', 'Progress window')}
      </p>

      <p
        id="headline-band-title"
        className="font-serif italic text-white/90 mb-7 max-w-2xl leading-[1.45] relative"
        style={{ fontSize: mobile ? 17 : 22 }}
      >
        {displayNarrative}
      </p>

      <div className="relative" style={gridStyle}>
        <StatCell
          gridArea="issues"
          num={issuesNum}
          label={t('headline.issues.label', 'Issues fixed')}
        />
        <StatCell
          gridArea="visits"
          num={visitsNum}
          label={t('headline.visits.label', 'Daily visits gained')}
        />
        <StatCell
          gridArea="score"
          num={scoreNum}
          label={t('headline.score.label', 'Overall score')}
        />
        <StatCell
          gridArea="citation"
          num={citationNum}
          label={t('headline.citation.label', 'AI citation rate')}
          bottom={!mobile}
          infoId="ai-citation-rate"
          infoDescription={t(
            'tooltip.citationRate',
            'The percentage of AI-generated responses that include a link or reference to your domain. Measured across ChatGPT, Google AI Overviews, Claude, and more.',
          )}
          glossaryAnchor="ai-citation-rate"
        />
        <StatCell
          gridArea="mention"
          num={mentionNum}
          label={t('headline.mention.label', 'AI mention share')}
          bottom={!mobile}
          newBadge
          infoId="ai-mention-share"
          infoDescription={t(
            'tooltip.mentionShare',
            "When someone asks ChatGPT, Perplexity, Claude, or Google's AI Overviews about your category, how often does your name come up? We track 100+ relevant questions and count how many answers mention you versus your competitors. Industry-standard term: Share of Model Voice.",
          )}
          glossaryAnchor="share-of-model-voice"
        />
      </div>
    </section>
  );
}
